import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getIconUrl, getSvgIconUrl } from './index';

dayjs.extend(utc);

const num = (x) => {
	const n = Number(x);
	return Number.isFinite(n) ? n : undefined;
};

const toLayerKey = (raw) => {
	if (!raw) return '';
	return String(raw).trim().replace(/_/g, ' ').replace(/\s+/g, ' ').toLowerCase();
};

const isRainGaugeLayerKey = (layerKey) => layerKey === 'rain gauge';

const cache = new Map();
const getThreshholds = (rawData) => {
	if (!rawData) return Promise.resolve(rawData);

	const mt = Array.isArray(rawData)
		? (rawData.find((p) => p?.config?.isPrimary) ?? rawData[0])
		: rawData;

	if (!mt) return Promise.resolve(rawData);

	const parameterId = num(mt?.measurementTargetId ?? mt?.id);
	if (!Number.isFinite(parameterId)) return Promise.resolve(mt);

	if (cache.has(parameterId)) return cache.get(parameterId);

	const thresholds = mt.thresholds ?? {};
	const high = thresholds.high ?? {};
	const low = thresholds.low ?? {};

	const result = {
		...mt,
		hcritical: num(mt?.hcritical ?? high.risk),
		hhCritical: num(mt?.hhCritical ?? high.impacted),
		lowCritical: num(mt?.lowCritical ?? low.risk),
		llowCritical: num(mt?.llowCritical ?? low.impacted),
		valueUnit: mt?.valueUnit ?? mt?.unit ?? mt?.config?.unit,
	};

	const p = Promise.resolve(result);
	cache.set(parameterId, p);
	return p;
};
const getAssetStatus = (mt) => {
	const v = num(
		mt?.currentValue ??
			mt?.dataSources?.lastReading?.value ??
			(Array.isArray(mt?.dataSources) ? mt.dataSources[0]?.lastReading?.value : undefined),
	);

	// TODO: Revisit when logic is agreed on
	// if (!mt) return 'OFFLINE';
	// if (mt.expired === true) return 'OFFLINE';

	const atRiskHigh = num(mt?.hcritical ?? mt?.thresholds?.high?.risk);
	const impactedHigh = num(mt?.hhCritical ?? mt?.thresholds?.high?.impacted);

	// TODO: Revisit when logic is agreed on
	// const atRiskLow    = num(mt.lowCritical);
	// const impactedLow  = num(mt.llowCritical);

	const impacted = Number.isFinite(impactedHigh) && v >= impactedHigh;

	// TODO: Revisit when logic is agreed on
	// || (Number.isFinite(impactedLow)  && v <  impactedLow);

	if (impacted) return 'IMPACTED';

	const atRisk = Number.isFinite(atRiskHigh) && v >= atRiskHigh;

	// TODO: Revisit when logic is agreed on
	// || (Number.isFinite(atRiskLow)  && v <  atRiskLow);

	if (atRisk) return 'AT_RISK';

	return 'ONLINE';
};

const isWithin = (timestamp, timeWindows) => {
	return timeWindows.some(([start, end]) => timestamp >= start && timestamp <= end);
};

export const getImpactStatus = (assetMap, assetIds, timestamp) => {
	if (!assetMap || !Array.isArray(assetIds) || !Number.isFinite(timestamp)) return null;

	let anyEntry = false;

	for (const id of assetIds) {
		const entry = assetMap.get(id);
		if (!entry) continue;

		anyEntry = true;

		const risks = Array.isArray(entry.risks) ? entry.risks : [];
		const impacts = Array.isArray(entry.impacts) ? entry.impacts : [];

		if (isWithin(timestamp, impacts)) return 'IMPACTED';
		if (isWithin(timestamp, risks)) return 'AT_RISK';
	}

	return anyEntry ? 'ONLINE' : null;
};

const pickLastCoord = (v) => (Array.isArray(v) ? v.at(-1) : v);

export const parseAssetData = (
	data,
	cluster = true,
	onGetSvgIconUrl = getSvgIconUrl,
	opts = {},
) => {
	const predictedAt = Number.isFinite(opts?.predictedAt) ? opts.predictedAt : null;
	const forecastIndex = opts?.forecastIndex ?? null;

	if (cluster) {
		const toFeature = async (asset) => {
			const measureTarget = await getThreshholds(asset.mainMeasurementTarget ?? asset.parameters);
			let status = getAssetStatus(measureTarget);
			const ids = asset?.id != null ? [asset.id] : [];

			if (predictedAt !== null) {
				const impactStatus = getImpactStatus(forecastIndex, ids, predictedAt);
				if (impactStatus != null) {
					status = impactStatus;
				} else {
					status = 'ONLINE';
				}
			}

			const rawType = asset.infrastructureTypeName ?? asset.type;
			const layerKey = toLayerKey(rawType);
			const isRainGauge = isRainGaugeLayerKey(layerKey);
			const iconUrl = await onGetSvgIconUrl(rawType, status);
			const { latitude, longitude } = pickLastCoord(asset?.coordinates) || {};
			return {
				type: 'Feature',
				properties: {
					cluster: false,
					id: asset.measurementeInfraestructureId ?? asset.id,
					idVariants: ids.map(String),
					name: asset.measurementInfraestructureName ?? asset.name,
					type: rawType,
					layerKey,
					isRainGauge,
					icon: iconUrl,
					measurementTarget: measureTarget,
					longitude,
					latitude,
					status,
				},
				geometry: {
					type: 'Point',
					coordinates: [parseFloat(longitude), parseFloat(latitude)],
				},
			};
		};

		return Promise.all(data.map(toFeature));
	}

	return data.reduce((acc, curr) => {
		let status = getAssetStatus(curr.mainMeasurementTarget ?? curr.parameters);
		const ids = curr?.id != null ? [curr.id] : [];
		
		if (predictedAt !== null) {
			const impactStatus = getImpactStatus(forecastIndex, ids, predictedAt);
			if (impactStatus != null) {
				status = impactStatus;
			} else {
				status = 'ONLINE';
			}
		}

		const rawType = curr.infrastructureTypeName ?? curr.type;
		const layerKey = toLayerKey(rawType);
		const isRainGauge = isRainGaugeLayerKey(layerKey);
		const imgUrl = getIconUrl(rawType, status);
		const { latitude, longitude } = pickLastCoord(curr?.coordinates) || {};
		const assetObj = {
			id: curr.measurementeInfraestructureId ?? curr.id,
			idVariants: ids.map(String),
			name: curr.measurementInfraestructureName ?? curr.name,
			latitude,
			longitude,
			type: rawType,
			layerKey,
			isRainGauge,
			icon: imgUrl,
			measurementTarget: curr.mainMeasurementTarget ?? curr.parameters,
			status,
		};
		(acc[rawType] ||= []).push(assetObj);
		return acc;
	}, {});
};
